/**
 * Sélecteur d'émojis pour le chat — encapsule `emoji-picker-react`.
 *
 * Rendu via createPortal(document.body) pour ne PAS être clippé par un parent
 * en overflow-hidden (ex : le drawer chat). Positionné dynamiquement
 * au-dessus du bouton smiley grâce à getBoundingClientRect().
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Smile } from 'lucide-react';

const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

interface Props {
  onSelect: (emoji: string) => void;
  isDark?: boolean;
}

const PICKER_W = 320;
const PICKER_H = 380;

export const ChatEmojiPicker: React.FC<Props> = ({ onSelect, isDark = false }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const positionner = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    // Ancre bas-droit du bouton, panel au-dessus + aligné à droite du bouton.
    // Clamping pour rester visible dans le viewport.
    const marge = 8;
    let left = r.right - PICKER_W;
    if (left < marge) left = marge;
    if (left + PICKER_W > window.innerWidth - marge) {
      left = Math.max(marge, window.innerWidth - PICKER_W - marge);
    }
    let top = r.top - PICKER_H - 8;
    if (top < marge) top = r.bottom + 8;   // fallback en dessous si pas de place au-dessus
    if (top + PICKER_H > window.innerHeight - marge) {
      top = Math.max(marge, window.innerHeight - PICKER_H - marge);
    }
    setPos({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    positionner();
    const handler = () => positionner();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [open, positionner]);

  useEffect(() => {
    if (!open) return;
    const onClickOut = (e: MouseEvent) => {
      if (panelRef.current && panelRef.current.contains(e.target as Node)) return;
      if (btnRef.current && btnRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, [open]);

  const panel = open && pos ? createPortal(
    <div
      ref={panelRef}
      className="z-[9999] shadow-2xl rounded-xl overflow-hidden"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: PICKER_W }}
    >
      <React.Suspense fallback={
        <div className={`rounded-xl p-4 text-xs ${
          isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700'
        }`}>Chargement…</div>
      }>
        <EmojiPicker
          theme={(isDark ? 'dark' : 'light') as any}
          onEmojiClick={(data: any) => {
            onSelect(data.emoji);
            setOpen(false);
          }}
          lazyLoadEmojis
          width={PICKER_W}
          height={PICKER_H}
          searchPlaceHolder="Rechercher…"
          previewConfig={{ showPreview: false }}
          skinTonesDisabled
        />
      </React.Suspense>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`p-2 rounded-full transition-colors ${
          isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'
        }`}
        title="Emojis"
      >
        <Smile size={18} />
      </button>
      {panel}
    </>
  );
};

export default ChatEmojiPicker;
